/*
 * Display texts of the log entry type `early_warning` ("Frühwarnung"), written by the workflow-manager (author type System).
 * Every payload field is optional here, so incomplete or older entries still render.
 */
import {courseLine, mailStatusMeta, PresenceDot, Tone} from '@/components/earlyWarning/earlyWarning.helper';
import {
    AbsenceEarlyWarningMailSkipReason,
    AbsenceEarlyWarningMailStatus,
} from '@/enums/workflow-manager/AbsenceEarlyWarning.enum';
import {htmlToText} from '@/components/timeline/logEntryMeta';

export type EarlyWarningLogPayload = {
    message?: string;
    trigger?: string;
    recentStates?: Array<'A' | 'E' | 'U' | string>;
    course?: {
        customId?: string; subject?: string; dayOfWeek?: number; startTime?: number; endTime?: number;
        institutionName?: string; city?: string; state?: string;
    };
    rule?: { name?: string };
    toDo?: unknown;
    responsible?: { name?: string; source?: string };
    mail?: {
        status?: AbsenceEarlyWarningMailStatus; reason?: AbsenceEarlyWarningMailSkipReason;
        recipientType?: 'parent' | 'student'; recipient?: string; recipientName?: string; templateTitle?: string;
    };
};

const LETTER: Record<string, { text: string; tone: Tone }> = {
    A: {text: 'anwesend', tone: 'good'},
    E: {text: 'entschuldigt', tone: 'warn'},
    U: {text: 'unentschuldigt', tone: 'bad'},
};

/** Dots oldest → newest from `recentStates` (the payload lists the newest first) */
export const dotsFromLetters = (letters: string[] | undefined): PresenceDot[] => (letters || [])
    .slice()
    .reverse()
    .map((letter, index) => ({
        key: `${index}-${letter}`,
        short: LETTER[letter] ? letter : '?',
        text: LETTER[letter]?.text || 'unbekannt',
        tone: LETTER[letter]?.tone || 'muted',
        date: '',
    }));

/** "Regel „Fehlen nachfragen – BW“" */
export const earlyWarningTitle = (payload: EarlyWarningLogPayload): string =>
    payload.rule?.name ? `Regel „${payload.rule.name}“` : 'Frühwarnung ausgelöst';

/** "Gitarre KS012 · Di 14:00 · Adolf-Kußmaul-Schule, Graben-Neudorf" */
export const earlyWarningCourseLine = (payload: EarlyWarningLogPayload): string => courseLine({
    subjectName: payload.course?.subject,
    courseCustomId: payload.course?.customId,
    dayOfWeek: payload.course?.dayOfWeek,
    startTime: payload.course?.startTime,
    institution: {name: payload.course?.institutionName, city: payload.course?.city},
});

/** "Mail an Familie Mertens verschickt" / "keine Mail: keine Mail-Adresse hinterlegt" */
export const earlyWarningMailText = (payload: EarlyWarningLogPayload): { text: string; tone: Tone; icon: string } => {
    const mail = payload.mail;
    const meta = mailStatusMeta(mail);
    switch (mail?.status) {
        case AbsenceEarlyWarningMailStatus.SENT: {
            const to = mail.recipientType === 'student'
                ? (mail.recipientName ? `an ${mail.recipientName}` : 'an die Schüler:in')
                : (mail.recipientName ? `an ${mail.recipientName}` : 'an die Familie');
            return {text: `Mail ${to} verschickt`, tone: meta.tone, icon: meta.icon};
        }
        case AbsenceEarlyWarningMailStatus.SKIPPED:
        case AbsenceEarlyWarningMailStatus.FAILED: {
            const reason = meta.details.split(' · ')[0];
            const prefix = mail.status === AbsenceEarlyWarningMailStatus.FAILED ? 'Mail fehlgeschlagen' : 'keine Mail';
            return {text: reason ? `${prefix}: ${reason}` : prefix, tone: meta.tone, icon: meta.icon};
        }
        case AbsenceEarlyWarningMailStatus.DISABLED:
            return {text: 'keine Mail (in der Regel ausgeschaltet)', tone: meta.tone, icon: meta.icon};
        default:
            return {text: '', tone: 'muted', icon: meta.icon};
    }
};

/** Readable text of a log entry of an unknown type (fallback renderer) */
export const fallbackLogText = (payload: unknown): string => {
    if (!payload || typeof payload !== 'object') {
        return typeof payload === 'string' ? payload : '';
    }
    const p = payload as Record<string, unknown>;
    for (const key of ['message', 'summary', 'text', 'subject', 'description', 'trigger']) {
        const value = p[key];
        if (typeof value === 'string' && value.trim()) {
            return htmlToText(value);
        }
    }
    return '';
};
